import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  base64PaddingStatus,
  base64UrlHint,
  detectMagicBytes,
  estimateEntropy,
  resolveInputBytes,
  type ByteInputMode,
} from '../../../shared/analysis';
import { detectFormats } from '../../../shared/detection';
import { explainHexDump } from '../../../shared/explainers';
import { useWorkspace } from '../../hooks/useWorkspace';

const ROUTE_FOR_FORMAT: Record<string, string> = {
  hex: '/tool/hexdump',
  base64: '/tool/base64',
  base64url: '/tool/base64',
  binary: '/tool/binary',
  jwt: '/tool/jwt',
  urlencoded: '/tool/url',
  json: '/tool/format',
  text: '/tool/hex',
};

function shouldSuggestFormatter(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) return false;
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    return true;
  }
  if (trimmed.startsWith('<') && trimmed.includes('>')) {
    return true;
  }
  if (/^\s*[A-Za-z0-9_.-]+\s*:\s*.+/m.test(trimmed)) {
    return true;
  }
  if (/^\s*[A-Za-z0-9_.-]+\s*=\s*.+/m.test(trimmed)) {
    return true;
  }
  return false;
}

export function InspectorPage() {
  const { input, inputMode, file, setFile, setInput, setInputMode } = useWorkspace();
  const navigate = useNavigate();

  const candidates = useMemo(
    () => (inputMode === 'file' ? [] : detectFormats(input)),
    [input, inputMode],
  );
  const formatterShortcut = useMemo(
    () => inputMode !== 'file' && shouldSuggestFormatter(input),
    [input, inputMode],
  );
  const resolved = useMemo(() => {
    try {
      return { ...resolveInputBytes(input, inputMode, file?.bytes ?? null), error: '' };
    } catch (error) {
      return {
        bytes: new Uint8Array(),
        mode: 'text' as const,
        error: error instanceof Error ? error.message : 'Unable to read input',
      };
    }
  }, [file, input, inputMode]);
  const bytes = resolved.bytes;
  const entropy = useMemo(() => estimateEntropy(bytes), [bytes]);
  const magic = useMemo(() => detectMagicBytes(bytes), [bytes]);

  const chooseFile = async (nextFile: File | undefined) => {
    if (nextFile) await setFile(nextFile);
  };

  return (
    <section className="animate-rise space-y-3">
      <header>
        <h1 className="text-lg font-semibold text-slate-100">Universal Inspector</h1>
        <p className="text-sm text-slate-400">
          Paste or drop unknown payloads and Hexyr suggests how to decode, inspect, and validate.
        </p>
        {formatterShortcut && (
          <button
            type="button"
            className="focus-ring mt-2 rounded border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-xs text-cyan-200"
            onClick={() => navigate('/tool/format')}
          >
            Open in Formatter Lab
          </button>
        )}
      </header>

      <div
        className="glass rounded-md p-3"
        onDrop={async (event) => {
          event.preventDefault();
          const file = event.dataTransfer.files[0];
          if (!file) return;
          await setFile(file);
        }}
        onDragOver={(event) => event.preventDefault()}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <label>
            Input mode
            <select
              className="ml-2 rounded border border-white/10 bg-surface-800 px-2 py-1"
              value={inputMode}
              onChange={(event) => setInputMode(event.target.value as ByteInputMode)}
            >
              <option value="auto">Auto</option>
              <option value="hex">Hex</option>
              <option value="text">Text</option>
              <option value="base64">Base64</option>
              <option value="file">File</option>
            </select>
          </label>
          <label className="focus-ring cursor-pointer rounded border border-white/10 bg-surface-800 px-3 py-1.5 text-slate-200">
            Choose binary file
            <input
              className="sr-only"
              type="file"
              onChange={(event) => void chooseFile(event.currentTarget.files?.[0])}
            />
          </label>
          <span className="text-slate-500">or drop a file anywhere in this panel</span>
        </div>
        {inputMode === 'file' ? (
          <div className="flex min-h-32 flex-col items-center justify-center rounded-md border border-dashed border-cyan-400/30 bg-cyan-500/5 p-4 text-center">
            {file ? (
              <>
                <strong className="text-cyan-200">{file.name}</strong>
                <span className="mt-1 text-xs text-slate-400">
                  {file.size.toLocaleString()} bytes{file.type ? ` · ${file.type}` : ''}
                </span>
                <button
                  type="button"
                  className="focus-ring mt-3 rounded border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-xs text-cyan-200"
                  onClick={() => navigate('/tool/hexdump')}
                >
                  Open exact bytes in Hexdump
                </button>
              </>
            ) : (
              <span className="text-sm text-slate-400">Choose or drop a binary file.</span>
            )}
          </div>
        ) : (
          <textarea
            className="focus-ring h-[40vh] w-full resize-none rounded-md border border-white/10 bg-surface-900/60 p-3 font-mono text-sm text-slate-100"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Paste anything here: JWT, base64, hex, JSON, or URL-encoded text"
          />
        )}
      </div>

      {resolved.error && (
        <div className="rounded border border-red-400/40 bg-red-500/10 p-2 text-sm text-red-300">
          {resolved.error}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="glass rounded-md p-3">
          <h2 className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">
            Likely Formats
          </h2>
          <div className="space-y-2">
            {candidates.slice(0, 5).map((candidate) => (
              <button
                type="button"
                key={`${candidate.format}-${candidate.reason}`}
                className="focus-ring w-full rounded border border-white/10 bg-surface-800/50 p-2 text-left text-xs hover:border-cyan-400/50"
                onClick={() => navigate(ROUTE_FOR_FORMAT[candidate.format] ?? '/tool/hex')}
              >
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-100">{candidate.format}</span>
                  <span className="text-slate-400">{Math.round(candidate.confidence * 100)}%</span>
                </div>
                <p className="mt-1 text-slate-400">{candidate.reason}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="glass rounded-md p-3 text-xs">
          <h2 className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Analysis</h2>
          <div className="space-y-2 text-slate-300">
            {file && inputMode === 'file' && (
              <p>
                File: {file.name} ({file.size.toLocaleString()} bytes)
              </p>
            )}
            <p>Byte interpretation: {resolved.mode}</p>
            <p>Entropy estimate: {entropy.toFixed(3)} bits/byte</p>
            {inputMode !== 'file' && (
              <>
                <p>Base64 padding: {base64PaddingStatus(input)}</p>
                <p>Base64 variant hint: {base64UrlHint(input)}</p>
                <p>Hex explainer: {explainHexDump(input)}</p>
              </>
            )}
            {magic && (
              <p>
                Magic bytes: <strong>{magic.name}</strong> ({magic.extension}) - {magic.description}
              </p>
            )}
            {input.includes('.') && (
              <p className="text-amber-300">JWT decode is not signature verification.</p>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}
